import type { CallTopic } from './api/callsApi';

// Converts between the editable markdown text shown in the builder and the
// structured CallTopic[] that's actually persisted (RetellCallAgent.topicsJson).
// Users paste/write content as markdown (often generated by an LLM) instead of
// filling in separate Topic/Questions/Response fields — this is the single
// place that reconciles the two shapes.
//
// Expected block format, repeated per topic and separated by "---":
//
//   ## Topic name
//
//   Possible Questions:
//   - question one
//   - question two
//
//   Response:
//
//   The agent's full answer.

// Matches the whole label line (plus its trailing newline) so the section
// body can be sliced by match end — NOT by dropping "line 0" after a
// line-array split, which breaks when a blank line precedes the label
// (`\s*` in a naive regex swallows the blank line too, shifting everything).
const QUESTIONS_LABEL = /^[ \t]*possible questions:?[ \t]*\n?/im;
const RESPONSE_LABEL = /^[ \t]*response:?[ \t]*\n?/im;

export function parseTopicsMarkdown(markdown: string): { topics: CallTopic[]; warnings: string[] } {
  const warnings: string[] = [];
  const text = markdown.replace(/\r\n/g, '\n');

  // Split on lines starting with "## " (topic headers). Anything before the
  // first one — e.g. a pasted "# Practice Name" / "# ... Knowledge Base"
  // title, or a leading "---" — isn't part of a topic and is dropped.
  const blocks = text.split(/\n(?=##\s+)/g).filter((b) => /^##\s+/.test(b.trim()));

  const topics: CallTopic[] = blocks.map((block, idx) => {
    const lines = block.split('\n');
    const topicLine = lines[0].replace(/^##\s*/, '').trim();
    const label = topicLine || `Topic ${idx + 1}`;

    const body = lines
      .slice(1)
      .filter((l) => l.trim() !== '---')
      .join('\n');

    const qMatch = body.match(QUESTIONS_LABEL);
    const rMatch = body.match(RESPONSE_LABEL);
    const qIdx = qMatch?.index ?? -1;
    const rIdx = rMatch?.index ?? -1;

    let questions: string[] = [];
    if (qMatch && qMatch.index !== undefined) {
      const sectionStart = qMatch.index + qMatch[0].length;
      const sectionEnd = rIdx !== -1 && rIdx > qIdx ? rIdx : body.length;
      questions = body
        .slice(sectionStart, sectionEnd)
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.startsWith('-') || l.startsWith('*'))
        .map((l) => l.replace(/^[-*]\s*/, '').trim())
        .filter(Boolean);
    }

    let response = '';
    if (rMatch && rMatch.index !== undefined) {
      response = body.slice(rMatch.index + rMatch[0].length).trim();
    } else {
      // No explicit "Response:" label yet (mid-edit) — fall back to whatever
      // text isn't part of the questions block so it doesn't just vanish.
      response = (qIdx !== -1 ? body.slice(0, qIdx) : body).trim();
    }

    if (!topicLine) warnings.push(`Topic ${idx + 1} is missing a name.`);
    if (!questions.length) warnings.push(`"${label}" has no possible questions listed.`);
    if (!response) warnings.push(`"${label}" has no response.`);

    return { topic: topicLine, questions, response };
  });

  if (!topics.length && text.trim()) {
    warnings.push('No topics found — start each one with "## Topic name".');
  }

  return { topics, warnings };
}

export function topicsToMarkdown(topics: CallTopic[]): string {
  return topics
    .map((t) => {
      const lines = [`## ${t.topic}`, ''];
      if (t.questions.length) {
        lines.push('Possible Questions:', ...t.questions.map((q) => `- ${q}`), '');
      }
      lines.push('Response:', '', t.response);
      return lines.join('\n');
    })
    .join('\n\n---\n\n');
}
